import '@/styles/globals.css';
import type { AppProps } from 'next/app';
import { processSeoDataForRegistry, createPlaceholderMap } from '@/lib/seoHelpers';
import { SeoScriptRegistry, resetScriptRegistry } from '@/lib/seoScriptRegistry';
import { retrackWhatConverts } from '@/lib/whatconverts';
import Head from 'next/head';
import Script from 'next/script';
import { useRouter } from 'next/router';
import { useEffect } from 'react';
import localFont from 'next/font/local';
import SWRProvider from '@/providers/SWRProvider';
import { getCacheKey } from '@/hooks/useDirectus';

const brandonGrotesque = localFont({
	src: [
		{
			path: '../../public/fonts/BrandonGrotesque-Light.woff2',
			weight: '300',
			style: 'normal',
		},
		{
			path: '../../public/fonts/BrandonGrotesque-Regular.woff2',
			weight: '400',
			style: 'normal',
		},
		{
			path: '../../public/fonts/BrandonGrotesque-Medium.woff2',
			weight: '500',
			style: 'normal',
		},
		{
			path: '../../public/fonts/BrandonGrotesque-Bold.woff2',
			weight: '700',
			style: 'normal',
		},
	],
	variable: '--font-brandon',
	display: 'swap',
	preload: true,
})

const agopus = localFont({
	src: '../../public/fonts/Cardillac-Light.woff2',
	variable: '--font-agopus',
	display: 'swap',
	preload: true,
})

function App({ Component, pageProps }: AppProps) {
  // Debug logging
  console.log('App pageProps.page structure:', pageProps.page ? Object.keys(pageProps.page) : 'no page data');
  if (pageProps.page?.patient) {
    console.log('Patient data:', { id: pageProps.page.patient.id, title: pageProps.page.patient.title });
  }
  if (pageProps.page?.procedure) {
    console.log('Procedure data:', { title: pageProps.page.procedure.title });
  }
  console.log('SEO data available:', !!pageProps.page?.seo);

  // Process SEO data on the server side
  if (typeof window === 'undefined') {
    // Always reset registry between requests — including 404/error pages where page is undefined.
    // Without this unconditional reset, the canonical and OG tags from the previous CMS page
    // rendered in the same Node.js process would leak into 404 responses.
    resetScriptRegistry();
    if (pageProps.page?.seo) {
      processSeoDataForRegistry(pageProps.page.seo, pageProps.page, pageProps.page.version === 'staging');
    }
  }
  
  // Re-run the WhatConverts number swap + lead-URL re-point on every SPA
  // navigation. The native tracker only runs its one-shot swap on hard load, so
  // pages reached via in-app routing keep the default number and mis-attribute
  // leads to the landing URL. See doc/whatconverts-spa-retrack.md.
  const router = useRouter();
  useEffect(() => {
    // Run once on mount so the swap + MutationObserver are active on the initial
    // hard load (Next only fires routeChangeComplete for in-app navigations).
    retrackWhatConverts();
    router.events.on('routeChangeComplete', retrackWhatConverts);
    return () => {
      router.events.off('routeChangeComplete', retrackWhatConverts);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Get the default page title from page data
  const defaultTitle = pageProps.page?.title || '';
  const isStaging = pageProps.page?.version === 'staging';
  const isForApproval = pageProps.page?.status === 'forapproval';
  const shouldNoIndex = isForApproval || pageProps.page?.slug === 'thank-you';

  // Create placeholder map for template variable replacement
  const placeholderMap = pageProps.page ? createPlaceholderMap(pageProps.page) : {};

  // Helper function to replace all placeholders in a string
  const replacePlaceholders = (text: string): string => {
    if (!text) return text;
    return text.replace(/{([^{}]+)}/g, (match, variable) => {
      // Check placeholder map first
      if (placeholderMap[variable] !== undefined) {
        return placeholderMap[variable];
      }
      // Fallback to title for backward compatibility
      if (variable === 'title') {
        return defaultTitle;
      }
      return match;
    });
  };

  // Initialize SEO values
  let metaTitle = isStaging ? '(Staging Version) ' + defaultTitle : defaultTitle;
  let metaDescription = '';

  // Extract SEO values from entries if available
  if (pageProps.page?.seo && Array.isArray(pageProps.page.seo)) {
    for (const seoItem of pageProps.page.seo) {
      if (!seoItem.seo_collection_id?.entries || !Array.isArray(seoItem.seo_collection_id?.entries)) {
        continue;
      }

      // Search through all entries for both title and description
      for (const entry of seoItem.seo_collection_id.entries) {
        if (!entry.seo_entries_id) continue;

        // Handle SEO title
        if (entry.seo_entries_id.type === 'title' || entry.seo_entries_id.type === 'page_title') {
          let titleContent = entry.seo_entries_id.entry || '';
          // Replace all placeholders including {procedureTitle}, {categoryTitle}, etc.
          titleContent = replacePlaceholders(titleContent);
          metaTitle = isStaging ? '(Staging Version) ' + titleContent : titleContent;
        }

        // Handle SEO description
        if (entry.seo_entries_id.type === 'description') {
          metaDescription = entry.seo_entries_id.entry || '';
          // Replace all placeholders
          metaDescription = replacePlaceholders(metaDescription);
        }
      }
    }
  }

  // Get base URL dynamically
  const baseUrl = process.env.NEXT_PUBLIC_BASE_URL || 
                  (process.env.VERCEL_URL ? `https://${process.env.VERCEL_URL}` : undefined);
  const directusUrl = process.env.NEXT_PUBLIC_DIRECTUS_URL;
  const directusOrigin = directusUrl ? new URL(directusUrl).origin : undefined;

	const swrFallback: Record<string, unknown> = {};
	const pageSlug = pageProps.page?.slug || '';
	if (pageSlug) {
		const pageKey = getCacheKey.page(pageSlug);
		if (pageKey) {
			swrFallback[pageKey] = pageProps.page;
		}
	}

  return (
		<SWRProvider fallback={swrFallback}>
			<Head>
				{metaTitle && <title>{metaTitle}</title>}
				{metaDescription && (
					<meta name='description' content={metaDescription} />
				)}
				<meta name='viewport' content='width=device-width, initial-scale=1' />
				<meta name="google-site-verification" content="JQ7z5Hl-vlIyxDxWTiceN1D5ckjW_Q6Fo8ao1tgmzDY" />
				{/* noindex */}
				<meta name='robots' content={shouldNoIndex ? 'noindex, nofollow' : 'index, follow'} />
				{baseUrl && (
					<link
						rel='preconnect'
						href={baseUrl}
						crossOrigin='anonymous'
					/>
				)}
				{directusOrigin && (
					<link
						rel='preconnect'
						href={directusOrigin}
						crossOrigin='anonymous'
					/>
				)}
			</Head>

			{/* Client-side scripts using Next.js Script component */}
			<SeoScriptRegistry.Client />
			<Script
				src='https://static.elfsight.com/platform/platform.js'
				strategy='lazyOnload'
			/>
			
			{process.env.NEXT_PUBLIC_VERCEL_ENV !== 'production' && (
				<Script
					src='https://www.bugherd.com/sidebarv2.js?apikey=pxx9wwwmsxptb0b95bh01w'
					strategy='afterInteractive'
				/>
			)}
			<Script
				src='https://acsbapp.com/apps/app/dist/js/app.js'
				strategy='afterInteractive'
				onLoad={() => {
					;(window as any)?.acsbJS?.init?.()
				}}
			/>

			<main
				className={`${brandonGrotesque.variable} ${agopus.variable} font-brandon font-light`}
			>
				<Component {...pageProps} />
			</main>
		</SWRProvider>
	)
}

export default App;
